import { useCallback } from 'react';
import { format } from 'date-fns';
import { useApolloClient } from '@apollo/client';
import { TourActionType } from '@zg-rentals/ts-z-calendar-client';
import { logError } from '@zg-rentals/log-error';
import usePageIdentifier from './usePageIdentifier';
import { PAGE_NAME } from '../constants/pageName';
import { STATUS_LABEL_TOUR, STATUS_LABEL_TOUR_CANCELED, STATUS_LABEL_TOUR_DECLINED } from '../components/StatusLabel';

export const STATUS_LABELS = {
  [TourActionType.IRENE_CANCEL]: {
    linkId: STATUS_LABEL_TOUR_CANCELED,
    text: 'TOUR CANCELED',
  },
  [TourActionType.IRENE_DECLINE]: {
    linkId: STATUS_LABEL_TOUR_DECLINED,
    text: 'TOUR DECLINED',
  },
  [TourActionType.IRENE_CONFIRM]: {
    linkId: STATUS_LABEL_TOUR,
    text: '', // Custom date
  },
};

type UpdateStatusLabelCache = {
  conversationId: string;
  listingAlias: string;
};

type UpdateStatusLabelCacheAction =
  | TourActionType.IRENE_CANCEL
  | TourActionType.IRENE_DECLINE
  | TourActionType.IRENE_CONFIRM;

export default ({ conversationId, listingAlias }: UpdateStatusLabelCache) => {
  const apolloClient = useApolloClient();
  const currentPage = usePageIdentifier();

  const updateStatusLabelCache = useCallback(
    (action: UpdateStatusLabelCacheAction, startMs?: number) => {
      try {
        const tourStatusLabel = startMs ? `${format(new Date(startMs), 'MMM d').toUpperCase()} TOUR` : '';

        const newStatusLabel = {
          text: action === TourActionType.IRENE_CONFIRM ? tourStatusLabel : STATUS_LABELS[action].text,
          linkId: STATUS_LABELS[action].linkId,
        };

        if (currentPage === PAGE_NAME.inbox) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({
              __typename: 'ZRM_Conversation',
              conversationId,
            }),
            fields: {
              statusLabel(existing) {
                return {
                  __typename: 'ZRM_StatusLabel',
                  ...existing,
                  ...newStatusLabel,
                };
              },
            },
          });
        } else if (currentPage === PAGE_NAME.leadManagement) {
          apolloClient.cache.modify({
            id: apolloClient.cache.identify({
              __typename: 'ZRM_LeadManagementLead',
              conversationId,
            }),
            fields: {
              statusLabel(existing) {
                return {
                  __typename: 'ZRM_StatusLabel',
                  ...existing,
                  ...newStatusLabel,
                };
              },
              nextStep(existing) {
                return {
                  __typename: 'ZRM_LeadManagementNextStep',
                  ...existing,
                  nextStepType: 'link',
                  link: {
                    linkId: 'nextStepSendMessage',
                    text: 'Message',
                    url: `/rental-manager/inbox/${listingAlias}/${conversationId}`,
                  },
                };
              },
            },
          });
        }
      } catch (error) {
        logError({
          error,
          context: {
            details: 'Error modifying status labels in the cache',
            currentPage,
            action,
            listingAlias,
            conversationId,
            startMs,
          },
        });
      }
    },
    [apolloClient, currentPage, conversationId, listingAlias],
  );

  return updateStatusLabelCache;
};
